import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { getLocales } from 'expo-localization';
import type { Lang, Snapshot } from '@hogar/shared';
import { Platform } from 'react-native';
import { api, setSignedOutHandler, setToken } from './api';
import { storage } from './storage';

const TOKEN_KEY = 'hl.session';
const LANG_KEY = 'hl.lang';

interface Session {
  ready: boolean;
  signedIn: boolean;
  /** The owner's language: from their account once signed in, before that from this phone. */
  lang: Lang;
  setLang: (lang: Lang) => void;
  signIn: (token: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Set during the operator's read-only "view as": when it ends. */
  viewingUntil: Date | null;
}

const SessionContext = createContext<Session | null>(null);
const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 5_000 } } });

/**
 * The operator's "view as" arrives as app.dejodicho.com/#view=<code> (from /panel). The code is
 * kept only in memory, never saved on this browser, so the owner's own sign-in here is untouched.
 */
function takeViewCode(): string | null {
  if (Platform.OS !== 'web' || typeof location === 'undefined' || !location.hash.startsWith('#view=')) return null;
  const code = location.hash.slice(6);
  history.replaceState(null, '', location.pathname);
  return code || null;
}

function deviceLang(): Lang {
  return getLocales()[0]?.languageCode === 'en' ? 'en' : 'es';
}

export function SessionProvider({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionInner>{children}</SessionInner>
    </QueryClientProvider>
  );
}

function SessionInner({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [ready, setReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [localLang, setLocalLang] = useState<Lang>(deviceLang());
  const [viewingUntil, setViewingUntil] = useState<Date | null>(null);

  useEffect(() => {
    (async () => {
      const [t, l] = await Promise.all([storage.get(TOKEN_KEY), storage.get(LANG_KEY)]);
      if (l === 'en' || l === 'es') setLocalLang(l);
      const view = takeViewCode();
      if (view) setViewingUntil(new Date(Date.now() + 30 * 60_000));
      setToken(view ?? t);
      setSignedIn(!!(view ?? t));
      setReady(true);
    })();
  }, []);

  const signOut = useCallback(async () => {
    // The end of a "view as" reloads the page, which brings back this browser's own sign-in.
    if (viewingUntil && Platform.OS === 'web') return location.replace('/');
    setToken(null);
    await storage.set(TOKEN_KEY, null);
    qc.clear();
    setSignedIn(false);
  }, [qc, viewingUntil]);

  useEffect(() => setSignedOutHandler(() => void signOut()), [signOut]);

  const signIn = useCallback(async (t: string) => {
    setToken(t);
    await storage.set(TOKEN_KEY, t);
    setSignedIn(true);
  }, []);

  const snapshot = useQuery({ queryKey: ['snapshot'], queryFn: () => api<Snapshot>('GET', '/snapshot'), enabled: ready && signedIn });
  const lang = snapshot.data?.owner.lang ?? localLang;

  const setLang = useCallback(
    (l: Lang) => {
      setLocalLang(l);
      void storage.set(LANG_KEY, l);
      if (signedIn && !viewingUntil) {
        qc.setQueryData<Snapshot>(['snapshot'], (s) => (s ? { ...s, owner: { ...s.owner, lang: l } } : s));
        void api('PATCH', '/me', { lang: l });
      }
    },
    [signedIn, qc, viewingUntil],
  );

  const value = useMemo(() => ({ ready, signedIn, lang, setLang, signIn, signOut, viewingUntil }), [ready, signedIn, lang, setLang, signIn, signOut, viewingUntil]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}

/** Everything the owner app shows. Refreshed after each change and every 10 seconds, so staff checks appear quickly. */
export function useSnapshot() {
  const { signedIn } = useSession();
  return useQuery({ queryKey: ['snapshot'], queryFn: () => api<Snapshot>('GET', '/snapshot'), enabled: signedIn, refetchInterval: 10_000 });
}

/** Runs a change on the cloud service, then refreshes the screens. */
export function useAction() {
  const qc = useQueryClient();
  return useCallback(
    async <T,>(run: () => Promise<T>): Promise<T> => {
      const out = await run();
      await qc.invalidateQueries({ queryKey: ['snapshot'] });
      return out;
    },
    [qc],
  );
}

export function useSetSnapshot() {
  const qc = useQueryClient();
  return (s: Snapshot) => qc.setQueryData(['snapshot'], s);
}
